import { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { ADMIN_MENU } from '@lp/layouts/AdminLayout/config';
import { AdminPermissions, ReleaseNotesPermissions } from '@/permissions';

export const useAdminPanelAccess = () => {
  const { checkPermission } = useContext(AuthContext);

  const getAdminLayoutPermissions = useCallback(
    (item: string) => {
      const tmdxUserPermissions =
        checkPermission(AdminPermissions.TmdxUsersUpdate) || checkPermission(AdminPermissions.TmdxUsersView);
      const txCenterPermissions =
        checkPermission(AdminPermissions.TxCenterCreate) || checkPermission(AdminPermissions.TxCenterView);
      const opoPermissions = checkPermission(AdminPermissions.OpoCreate) || checkPermission(AdminPermissions.OpoView);
      const hospitalPermissions =
        checkPermission(AdminPermissions.HospitalCreate) || checkPermission(AdminPermissions.HospitalView);
      const ocsConsolesPermissions =
        checkPermission(AdminPermissions.OcsConsolesCreate) ||
        checkPermission(AdminPermissions.OcsConsolesUpdate) ||
        checkPermission(AdminPermissions.OcsConsolesView);
      const ocsPerfusionsSetsPermissions = checkPermission(AdminPermissions.OcsPerfusionsSetsView);
      const nopHubPermissions =
        checkPermission(AdminPermissions.NopHubCreate) || checkPermission(AdminPermissions.NopHubView);
      const regionsPermissions =
        checkPermission(AdminPermissions.RegionsRead) || checkPermission(AdminPermissions.RegionsUpdate);
      const providersConfigurationPermissions =
        checkPermission(AdminPermissions.ProvidersConfigurationCreate) ||
        checkPermission(AdminPermissions.ProvidersConfigurationView) ||
        checkPermission(AdminPermissions.ProvidersConfigurationUpdate);
      const releaseNotesViewPermission = checkPermission(ReleaseNotesPermissions.Read);

      switch (item) {
        case 'TMDX Users':
          return tmdxUserPermissions;
        case 'Transplant Centers':
          return txCenterPermissions;
        case 'OPOs':
          return opoPermissions;
        case 'Donor Hospitals':
          return hospitalPermissions;
        case 'Devices':
          return ocsConsolesPermissions || ocsPerfusionsSetsPermissions;
        case 'OCS Consoles':
          return ocsConsolesPermissions;
        case 'OCS Perfusion Sets':
          return ocsPerfusionsSetsPermissions;
        case 'NOP Hubs':
          return nopHubPermissions;
        case 'Regions':
          return regionsPermissions;
        case 'Logistics providers':
          return providersConfigurationPermissions;
        case 'Configuration':
          return providersConfigurationPermissions;
        case 'Release Notes':
          return releaseNotesViewPermission;
        default:
          return true;
      }
    },
    [checkPermission],
  );

  return useMemo(
    () =>
      ADMIN_MENU.map((section) => {
        if (section.type === 'section') {
          return {
            ...section,
            hasPermission: getAdminLayoutPermissions(section.label),
            children: section.children.map((child) => ({
              ...child,
              hasPermission: getAdminLayoutPermissions(child.label),
            })),
          };
        }

        return {
          ...section,
          hasPermission: getAdminLayoutPermissions(section.label),
        };
      }),
    [getAdminLayoutPermissions],
  );
};
